import './App.css';
import './Landing.css';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TOOLS_BY_CATEGORY, TOTAL_TOOL_COUNT, extractSteamId } from './toolData';

const CAT_SHORT = { 'cs2-stats': 'stats', 'faceit': 'faceit', 'inventory': 'inventory', 'vac-ban': 'bans', 'steam-profile': 'profiles' };

const OLD_TLD = '.com';
const NEW_TLD = '.vodka';
const TIMING = { idle: 2000, selecting: 400, deleted: 150, typeChar: 100, complete: 2500 };

function UrlAnimation() {
  const [phase, setPhase] = useState('idle');
  const [typedCount, setTypedCount] = useState(0);
  const timeoutRef = useRef(null);
  const reducedMotion = useRef(
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    if (reducedMotion.current) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    switch (phase) {
      case 'idle':
        setTypedCount(0);
        timeoutRef.current = setTimeout(() => setPhase('selecting'), TIMING.idle);
        break;
      case 'selecting':
        timeoutRef.current = setTimeout(() => setPhase('deleted'), TIMING.selecting);
        break;
      case 'deleted':
        timeoutRef.current = setTimeout(() => setPhase('typing'), TIMING.deleted);
        break;
      case 'typing':
        if (typedCount < NEW_TLD.length) {
          timeoutRef.current = setTimeout(() => setTypedCount(c => c + 1), TIMING.typeChar);
        } else {
          setPhase('complete');
        }
        break;
      case 'complete':
        timeoutRef.current = setTimeout(() => setPhase('rev-selecting'), TIMING.complete);
        break;
      case 'rev-selecting':
        timeoutRef.current = setTimeout(() => setPhase('rev-deleted'), TIMING.selecting);
        break;
      case 'rev-deleted':
        setTypedCount(0);
        timeoutRef.current = setTimeout(() => setPhase('rev-typing'), TIMING.deleted);
        break;
      case 'rev-typing':
        if (typedCount < OLD_TLD.length) {
          timeoutRef.current = setTimeout(() => setTypedCount(c => c + 1), TIMING.typeChar);
        } else {
          setPhase('idle');
        }
        break;
      default:
        break;
    }

    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, [phase, typedCount]);

  if (reducedMotion.current) {
    return (
      <div className="url-animation-container">
        <div className="url-animation-line">
          <span className="url-base">steamcommunity</span>
          <span className="url-tld-new">{NEW_TLD}</span>
          <span className="url-path">/profiles/76561198386265483</span>
        </div>
      </div>
    );
  }

  let tldContent = null;
  const cursorBlinks = phase === 'idle' || phase === 'complete';

  if (phase === 'idle') {
    tldContent = <span className="url-tld">{OLD_TLD}</span>;
  } else if (phase === 'selecting') {
    tldContent = <span className="url-tld url-tld-selected">{OLD_TLD}</span>;
  } else if (phase === 'deleted') {
    tldContent = null;
  } else if (phase === 'typing') {
    tldContent = <span className="url-tld-new">{NEW_TLD.slice(0, typedCount)}</span>;
  } else if (phase === 'complete') {
    tldContent = <span className="url-tld-new url-tld-glow">{NEW_TLD}</span>;
  } else if (phase === 'rev-selecting') {
    tldContent = <span className="url-tld-new url-tld-selected">{NEW_TLD}</span>;
  } else if (phase === 'rev-deleted') {
    tldContent = null;
  } else if (phase === 'rev-typing') {
    tldContent = <span className="url-tld">{OLD_TLD.slice(0, typedCount)}</span>;
  }

  return (
    <div className="url-animation-container">
      <a href="https://steamcommunity.vodka/profiles/76561198386265483" target="_blank" rel="noopener noreferrer" className="url-animation-line" data-track="landing-redirect-example">
        <span className="url-base">steamcommunity</span>
        {tldContent}
        <span className={`url-cursor ${cursorBlinks ? 'url-cursor-blink' : ''}`} />
        <span className="url-path">/profiles/76561198386265483</span>
      </a>
    </div>
  );
}

function Landing() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!searchInput.trim() || isResolving) return;

    setIsResolving(true);
    setError('');
    try {
      const resolved = await extractSteamId(searchInput);
      if (resolved) {
        navigate(`/profile/${resolved}`);
      }
    } catch {
      setError('Failed to resolve. Try a Steam64 ID directly.');
    } finally {
      setIsResolving(false);
    }
  };

  return (
    <div className="container">
      <div className="landing-fold">
        <div className="landing-hero">
          <h1>// player.properties</h1>
          <div className="landing-subtitle"># {TOTAL_TOOL_COUNT} tools. one page. one link.</div>
        </div>

        <div className="landing-redirect-hero">
          <div className="profile-comment">// just change the TLD</div>

          <UrlAnimation />

        </div>

        <div className="landing-divider"></div>

        <div className="landing-search-section">
          <div className="profile-comment landing-search-label">// or just paste a link</div>
          <form onSubmit={handleSubmit} className="landing-search-form">
            <div className="landing-search-line">
              <span className="property-key">search.query</span>
              <span className="equals"> = </span>
              <input
                type="text"
                className="steamid-input landing-search-input"
                value={searchInput}
                onChange={(e) => { setSearchInput(e.target.value); setError(''); }}
                placeholder='"paste Steam ID or profile link"'
              />
              <button type="submit" className="landing-search-button" disabled={isResolving}>
                {isResolving ? 'resolving...' : '→ lookup'}
              </button>
            </div>
            {error && <div className="landing-search-error"># {error}</div>}
          </form>
        </div>

        <div className="landing-tools-compact">
          <span className="property-key">tools.total</span>
          <span className="equals"> = </span>
          <span className="tools-count">{TOTAL_TOOL_COUNT}</span>
          <span className="tools-separator">|</span>
          {TOOLS_BY_CATEGORY.map((cat, i) => (
            <span key={cat.id} className="tools-cat-name">
              {CAT_SHORT[cat.id] || cat.id}
              {i < TOOLS_BY_CATEGORY.length - 1 && <span className="tools-dot"> · </span>}
            </span>
          ))}
        </div>

        <div className="landing-footer-section">
          <div className="landing-footer-links">
            <a href="/faq" className="faq-link">FAQ</a> &bull; <a href="/privacy" className="faq-link">Privacy</a> &bull; <a href="/feedback" className="faq-link">Feedback</a> &bull; <a href="https://ko-fi.com/oddlobster" className="faq-link" target="_blank" rel="noopener noreferrer">Support me with ☕</a>
          </div>
        </div>

        <button
          type="button"
          className="scroll-cue landing-seo-cue"
          onClick={() => {
            const el = document.getElementById('landing-about');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          aria-label="Scroll to more about player.properties"
        >
          <span className="scroll-cue-line"></span>
          <span className="scroll-cue-text">
            {'// find out more'}{' '}
            <span className="scroll-cue-arrow">↓</span>
          </span>
          <span className="scroll-cue-line"></span>
        </button>
      </div>

      <div id="landing-about" className="landing-seo-section">
        <div className="profile-comment">// what is this?</div>
        <p className="landing-seo-text">
          player.properties is a CS2 player profile aggregator. Paste any Steam ID, profile URL,
          or vanity name and get instant links to 39+ tools &mdash; CS2 stats, FACEIT ELO,
          inventory value, VAC ban history, Steam profile history, and more. One lookup,
          every platform.
        </p>
        <div className="profile-comment">// why use this instead of checking each site?</div>
        <p className="landing-seo-text">
          Instead of opening Leetify, csgostats, CSFloat, FACEIT, and Steam separately,
          player.properties pulls them all into a single dashboard. Or just swap the TLD in
          any steamcommunity.com link to .vodka &mdash; done.
        </p>
      </div>
    </div>
  );
}

export default Landing;
